import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { AuthRequest, StateManager, setTokenHelper } from 'orinabiji-auth-react';


export default class RequestHelper extends AuthRequest {
  private static ADMIN_BASE_URL = process.env.REACT_APP_ADMIN_API;
  private static CAS_BASE_URL = process.env.REACT_APP_CAS_API;
  public static RENEW_REDIRECT_URL = process.env.REACT_APP_RENEW_REDIDIRECT_URL;
  public static LOYALTY_BASE_URL = process.env.REACT_APP_LOYALTY_API;
  public static KARI_API = process.env.REACT_APP_KARI_API;

  private static _admin: AxiosInstance | null;
  private static _noAuthAdmin: AxiosInstance | null;
  private static _cas: AxiosInstance | null;
  private static _loyalty: AxiosInstance | null;
  private static _kari: AxiosInstance | null;
  private static _noAuthKari: AxiosInstance | null;

  private static getToken(): string {
    return localStorage.getItem('id_token') || '';
  }

  private static resetAxiosInstances() {
    this._cas = null;
    this._admin = null;
  }

  private static setInterceptor(instance: AxiosInstance, catchResponse: boolean = true) {
    instance.interceptors.request.use(
      (request): Promise<InternalAxiosRequestConfig> => {
        return new Promise((resolve, reject) => {
          //check token expiration time
          let tokenExpiration = 0;
          if (localStorage.getItem('user_info')) {
            const userInfo = JSON.parse(
              localStorage.getItem('user_info') || '{}'
            );
            tokenExpiration = userInfo.exp || 0;
            //in case if axios instances are created before record should be inserted in local storage
            if (!request.headers.Authorization || String(request.headers.Authorization).length === 7) {
              request.headers.Authorization = `Bearer ${this.getToken()}`;
              //reset axios instances for to set token
              this.resetAxiosInstances();
            }
          }
          const differenceToLocalTime =
            Number(localStorage.getItem('diff_to_local_time')) || 0;
          let delta = tokenExpiration - new Date().getTime() / 1000;
          if (differenceToLocalTime < 0) {
            delta -= differenceToLocalTime;
          }
          if (delta < 10) {
            this._authProvider.renewAuth(
              {
                redirectUri: this.RENEW_REDIRECT_URL
              },
              async (error, result) => {
                if (error && request.url !== '/users/checkSession') {
                  if (error.error === 'login_required' || error.error === 'interaction_required') {
                    setTimeout(() => {
                      this._authProvider.authorize({
                        state: StateManager.setState({ redirectUrlPath: window.location.pathname })
                      });
                    }, 0);
                  }
                  return;
                }
                if (result) {
                  request.headers.Authorization = `Bearer ${result.idToken}`;
                  this._token = result.idToken;
                  //empty request helper objects
                  this.resetAxiosInstances();

                  //set to local storage
                  await setTokenHelper(result);
                }
                return resolve(request);
              }
            );
          } else {
            return resolve(request);
          }
        });
      }
    );
    if (catchResponse) {
      instance.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error && error.response && error.response.status === 401) {
            localStorage.removeItem('id_token');
            localStorage.removeItem('user_info');
            this._authProvider.authorize({
              state: StateManager.setState({ redirectUrlPath: window.location.pathname })
            });
          }
          return Promise.reject(error.response?.data?.message);
        }
      );
    }
  }

  static get admin() {
    if (!this._admin) {
      this._admin = axios.create({
        baseURL: this.ADMIN_BASE_URL,
        headers: {
          'Project-Key': 'ADMIN',
          Authorization: `Bearer ${this.getToken()}`
        }
      });

      this.setInterceptor(this._admin);
    }

    return this._admin;
  }

  static get noAuthAdmin() {
    if (!this._noAuthAdmin) {
      this._noAuthAdmin = axios.create({
        baseURL: this.ADMIN_BASE_URL,
        headers: {
          'Project-Key': 'CAS'
        }
      });
    }

    return this._noAuthAdmin;
  }

  static get loyalty() {
    if (!this._loyalty) {
      this._loyalty = axios.create({
        baseURL: this.LOYALTY_BASE_URL,
        headers: {
          'Project-Key': 'LOYALTY',
          Authorization: `Bearer ${this.getToken()}`
        }
      });

      this.setInterceptor(this._loyalty);
    }

    return this._loyalty;
  }

  static get cas() {
    if (!this._cas) {
      this._cas = axios.create({
        baseURL: this.CAS_BASE_URL,
        headers: {
          'Project-Key': 'CAS',
          Authorization: `Bearer ${this.getToken()}`
        }
      });
      this.setInterceptor(this._cas);
    }

    return this._cas;
  }

  static get kari() {
    if (!this._kari) {
      this._kari = axios.create({
        baseURL: this.KARI_API,
        headers: {
          'Project-Key': 'ADMIN',
          Authorization: `Bearer ${this.getToken()}`
        }
      });

      this.setInterceptor(this._kari);
    }

    return this._kari;
  }

  static get noAuthKari() {
    if (!this._noAuthKari) {
      this._noAuthKari = axios.create({
        baseURL: this.KARI_API,
        headers: {
          'Project-Key': 'CAS'
        }
      });
    }

    return this._noAuthKari;
  }

  static get nonCasCatchResponse() {
    if (!this._cas) {
      this._cas = axios.create({
        baseURL: this.CAS_BASE_URL,
        headers: {
          'Project-Key': 'CAS',
          Authorization: `Bearer ${this.getToken()}`
        }
      });
      this.setInterceptor(this._cas, false);
    }

    return this._cas;
  }

}
